import { Link, useRouterState } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useState, type ReactNode } from 'react'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  cn,
  Icon,
  type IconName,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { useYeniSurum } from '../use-yeni-surum'
import { setAccessToken } from '../auth-store'
import { setTenantSlug } from '../tenant-store'
import { Guvenlik } from '../routes/guvenlik'
import { DuyuruBandi } from './duyuru-bandi'

/**
 * Menü artık koddan değil sunucudan geliyor (YES-122).
 *
 * Neden: dernek menüyü kendi düzenleyebilmeli (sıralama, etiket, gizleme,
 * role özel görünüm) ve düzenleme kiracıya ait bir veri. Varsayılan menü
 * yine kodda ama API tarafında (`modules/navigation/catalog.py`), çünkü
 * öğeleri süzen iki şey — izinler ve plan özellikleri — zaten orada.
 * Panelde ikinci bir liste tutmak, ikisinin sessizce ayrışması demekti.
 */

function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === 'dark')
  return (
    <Button
      variant="ghost"
      size="icon"
      className="group"
      aria-label={dark ? 'Açık temaya geç' : 'Koyu temaya geç'}
      onClick={() => {
        const next = !dark
        setDark(next)
        document.documentElement.dataset.theme = next ? 'dark' : 'light'
      }}
    >
      <Icon name={dark ? 'sun' : 'moon'} animated aria-hidden />
    </Button>
  )
}

/**
 * Panel kabuğu: kenar menü, üst bar, dernek seçici.
 *
 * Menü öğeleri derneğin planındaki özelliklere göre süzülür — planında Akademi
 * olmayan bir dernek menüde Akademi görmez, tıklayıp 403 almaz. İzin bazlı
 * süzme (bir Sayman'ın üye silememesi gibi) YES-85 ile buraya eklenecek.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const slug = useTenantSlug()
  const queryClient = useQueryClient()
  const yeniSurumVar = useYeniSurum()
  const path = useRouterState({ select: (state) => state.location.pathname })

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/auth/me')
      return data
    },
  })

  // Dernek ikinci adımı zorunlu kılmış olabilir (YES-171). Sunucu bu durumda
  // derneğe ait her uca 403 döner; kapıyı burada, o istekler hiç atılmadan
  // önce koyuyoruz. Kiracısız `/me/tenants` ayarı taşıyor.
  const { data: derneklerim } = useQuery({
    queryKey: ['me', 'tenants'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/me/tenants')
      return data ?? []
    },
  })
  const buDernek = derneklerim?.find((dernek) => dernek.slug === slug)
  const mfaKapisi = Boolean(buDernek?.settings.require_mfa) && me !== undefined && !me?.mfa_enabled
  // Ödeme gecikmesi (YES-176). Askıdaki dernekte sunucu her uca 403 döner;
  // kapıyı burada koyuyoruz ki kullanıcı boş ekran yerine ne olduğunu görsün.
  const askida = buDernek?.status === 'suspended'
  const odemeGecikti = buDernek?.status === 'past_due'

  const { data: permissions } = useQuery({
    queryKey: ['me', 'permissions', slug],
    enabled: Boolean(slug) && !mfaKapisi && !askida,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/me/permissions')
      return data ?? []
    },
  })

  const { data: subscription } = useQuery({
    queryKey: ['subscription', slug],
    // Abonelik `settings.read` istiyor; izni olmayan kullanıcıda 403 alıp
    // gereksiz hata göstermek yerine sorguyu hiç açmıyoruz.
    enabled: Boolean(slug) && (permissions ?? []).includes('settings.read'),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/subscription')
      return data
    },
  })

  const { data: menu } = useQuery({
    queryKey: ['navigation', slug],
    enabled: Boolean(slug) && !mfaKapisi && !askida,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/navigation')
      return data ?? []
    },
  })

  const items = menu ?? []

  function cikis() {
    void api.POST('/api/v1/auth/logout').finally(() => {
      setAccessToken(null)
      setTenantSlug(null)
      queryClient.clear()
    })
  }

  if (askida) {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <Card className="w-[min(32rem,100%)]">
          <CardHeader
            title="Ödeme gerekli"
            description={`${buDernek?.name ?? 'Derneğiniz'} panel erişimi geçici olarak kapalı.`}
          />
          <CardBody className="flex flex-col gap-3 text-sm">
            {/* İlk cümle "verileriniz duruyor": askıya alınan derneğin ilk
                korkusu bu ve cevabı ekranın en görünür yerinde olmalı. */}
            <p>
              <strong>Verileriniz duruyor.</strong> Üye kayıtlarınız, aidat ve belgeleriniz
              silinmedi; ödeme alındığında panel aynı gün yeniden açılır.
            </p>
            {buDernek?.status_reason && (
              <p className="text-[var(--ad-text-muted)]">{buDernek.status_reason}</p>
            )}
            <p className="text-[var(--ad-text-muted)]">
              Ödeme ve yeniden açma için destek ekibiyle iletişime geçin.
            </p>
            <div className="flex justify-between gap-2">
              <Button variant="secondary" onClick={() => setTenantSlug(null)}>
                Dernek değiştir
              </Button>
              <Button variant="ghost" onClick={cikis}>
                Çıkış
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (mfaKapisi) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-4 p-6">
        <div
          role="alert"
          className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--ad-warning)]/20 px-4 py-3 text-sm"
        >
          <Icon name="shield-alert" size={18} primaryColor="var(--ad-warning)" aria-hidden />
          <span>
            <strong>{buDernek?.name}</strong> yöneticileri için ikinci adım doğrulama zorunlu.
            Kurulumu tamamlayınca panel açılır.
          </span>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={cikis}>
            Çıkış
          </Button>
        </div>
        <Guvenlik />
      </div>
    )
  }

  return (
    <div className="grid min-h-dvh grid-cols-[15rem_1fr]">
      <aside className="border-r border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 py-4">
        <div className="px-2 pb-4">
          <p className="text-sm font-semibold">Akıllı Dernek</p>
          <p className="text-xs text-[var(--ad-text-muted)]">Yönetim paneli</p>
        </div>
        <nav className="flex flex-col gap-0.5">
          {items.map((item, index) => (
            <Fragment key={item.key}>
              {/* Grup başlığı, grup DEĞİŞTİĞİNDE yazılıyor — öğeleri gruba
                  göre yeniden kümelemek sıralamayı bozardı; sıra derneğin
                  kendi tercihi (YES-122). */}
              {item.group && item.group !== items[index - 1]?.group && (
                <p className="mt-3 px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  {item.group}
                </p>
              )}
              <Link
                to={item.to}
                className={cn(
                  // `group`: hover animasyonu ikonun kendi uzerinde degil,
                  // baglantinin herhangi bir yerinde tetiklensin.
                  'group flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-2 text-sm',
                  path === item.to
                    ? 'bg-[var(--ad-primary)]/12 text-[var(--ad-primary)]'
                    : 'text-[var(--ad-text-muted)] hover:bg-[var(--ad-surface-muted)]',
                  // Paketi kapalı öğe listede kalır ama soluk: dernek neyi
                  // satın alabileceğini görmeli (YES-122).
                  item.locked && 'opacity-55',
                )}
              >
                <Icon
                  name={item.icon as IconName}
                  animated
                  primaryColor={path === item.to ? 'var(--ad-primary)' : 'var(--ad-text-muted)'}
                  aria-hidden
                />
                {item.label}
                {item.locked && (
                  <Badge tone="neutral" className="ml-auto">
                    Paket kapalı
                  </Badge>
                )}
              </Link>
            </Fragment>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-col">
        {/* Yeni sürüm bandı (YES-90). Otomatik yenilemiyoruz: kullanıcı form
            doldurmuş olabilir. */}
        {yeniSurumVar && (
          <div className="flex items-center gap-2 bg-[var(--ad-primary)]/15 px-5 py-2 text-xs">
            <Icon name="refresh-cw" size={16} primaryColor="var(--ad-primary)" aria-hidden />
            <span>Yeni bir sürüm yayınlandı.</span>
            <Button size="sm" variant="ghost" onClick={() => window.location.reload()}>
              Yenile
            </Button>
          </div>
        )}
        {/* Ödeme gecikti ama erişim sürüyor (YES-176): askıya almadan önce
            görünür ama engellemeyen bir uyarı. */}
        {odemeGecikti && (
          <div
            role="alert"
            className="flex items-center gap-2 bg-[var(--ad-danger)]/15 px-5 py-2 text-xs text-[var(--ad-text)]"
          >
            <Icon name="shield-alert" size={16} primaryColor="var(--ad-danger)" aria-hidden />
            <span>
              <strong>Abonelik ödemeniz alınamadı.</strong> Ödeme yapılmazsa panel erişimi kapanır;
              verileriniz silinmez.
            </span>
          </div>
        )}
        {/* Platform duyuruları (YES-169): personelin herkese söylediği şey. */}
        <DuyuruBandi />
        {/* Kalıcı uyarı bandı: destek personeli bir dernek adına çalışırken
            bunun görünmemesi, yanlışlıkla kalıcı hasar vermeye açık kapı
            bırakır (YES-86). */}
        {me?.impersonation && (
          <div
            role="alert"
            className="flex items-center gap-2 bg-[var(--ad-warning)]/20 px-5 py-2 text-xs text-[var(--ad-text)]"
          >
            <Icon name="shield-alert" size={16} primaryColor="var(--ad-warning)" aria-hidden />
            <span>
              <strong>Destek oturumu.</strong> Bu dernek adına çalışıyorsunuz; yaptığınız her işlem
              denetim kaydına adınızla düşer.
            </span>
          </div>
        )}
        <header className="flex items-center gap-3 border-b border-[var(--ad-border)] bg-[var(--ad-surface)] px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{slug ?? 'Dernek seçilmedi'}</span>
            {subscription?.subscription.plan_name && (
              <Badge tone="info">{subscription.subscription.plan_name}</Badge>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* İkinci adım kapalıysa bu bir rozet değil, bir çağrı: derneğin
                üye verisine erişen bir hesap tek şifreyle korunuyor demek. */}
            {me && !me.mfa_enabled && (
              <Link
                to="/guvenlik"
                className="flex items-center gap-1.5 rounded-[var(--radius-control)] bg-[var(--ad-warning)]/15 px-2 py-1 text-xs"
              >
                <Icon name="shield-alert" size={14} primaryColor="var(--ad-warning)" aria-hidden />
                İkinci adım kapalı
              </Link>
            )}
            <Link
              to="/guvenlik"
              className="rounded-[var(--radius-control)] px-2 py-1 text-sm text-[var(--ad-text-muted)] hover:bg-[var(--ad-surface-muted)]"
            >
              Güvenlik
            </Link>
            <ThemeToggle />
            {slug && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setTenantSlug(null)
                  // Dernek değişti: önceki derneğin verisi gösterilmemeli.
                  void queryClient.resetQueries()
                }}
              >
                Dernek değiştir
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void api.POST('/api/v1/auth/logout').finally(() => {
                  setAccessToken(null)
                  setTenantSlug(null)
                  queryClient.clear()
                })
              }}
            >
              Çıkış
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-5">{children}</main>
      </div>
    </div>
  )
}
